import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { Institution } from '../types/Institution';
import { Course } from '../types/Course';
import { coursesQueryFunction } from '../services/adapters/queries/coursesQueryFunction';
import { useQuery } from 'react-query';
import {
  InstitutionTypeFilter,
  institutionsQueryFunction
} from '../services/adapters/queries/institutionsQueryFunction';
import { TeachersUIFilters } from '../types/TeachersUIFilters';
import { getInitialSearchTypeFromLocation } from '../utils/functions/getInitialSearchTypeFromLocation';

interface TeacherSearchContextProps {
  setCourse: (course: Course | undefined) => void;
  selectedCourse: Course | undefined;
  institutionCourses: Course[] | undefined;
  institutionCoursesLoading: boolean;

  institutions: Institution[] | undefined;
  setInstitution: (institution: Institution | undefined) => void;
  selectedInstitution: Institution | undefined;
  institutionsLoading: boolean;

  searchType: InstitutionTypeFilter;
  setSearchType: (type: InstitutionTypeFilter) => void;

  recentCourses: Course[];
  addRecentCourse: (course: Course | undefined) => void;
  clearRecentCourses: () => void;

  filters: TeachersUIFilters;
  setFilters: (filters: Partial<TeachersUIFilters>) => void;
  clearFilter: (key: keyof TeachersUIFilters) => void;
  clearAllFilters: () => void;
}

const TeacherSearchContext = createContext<TeacherSearchContextProps>({
  setCourse: () => {},
  selectedCourse: undefined,
  institutionCourses: undefined,
  institutionCoursesLoading: false,

  institutions: undefined,
  setInstitution: () => {},
  selectedInstitution: undefined,
  institutionsLoading: false,

  searchType: 'university',
  setSearchType: () => {},

  recentCourses: [],
  addRecentCourse: () => {},
  clearRecentCourses: () => {},

  filters: {},
  setFilters: () => {},
  clearFilter: () => {},
  clearAllFilters: () => {}
});

const useInstitutionsQuery = (searchType: InstitutionTypeFilter) => {
  const institutionsQuery = institutionsQueryFunction(searchType);
  const { data: institutions, isLoading: institutionsLoading } = useQuery<Institution[]>(
    institutionsQuery.queryKey,
    institutionsQuery.queryFn
  );

  return { institutions, institutionsLoading };
};

const useCoursesQuery = (institutionIdParam: string, searchType: InstitutionTypeFilter) => {
  const [institutionId, setInstitutionId] = useState(institutionIdParam);

  const query = coursesQueryFunction(institutionId, searchType);
  const { data: courses, isLoading: coursesLoading } = useQuery<Course[]>(
    query.queryKey,
    query.queryFn
  );

  return useMemo(
    () => ({
      institutionCourses: courses,
      institutionCoursesLoading: coursesLoading,
      setInstitutionId
    }),
    [setInstitutionId, courses, coursesLoading]
  );
};

export const useTeacherSearchContext = () => useContext(TeacherSearchContext);

interface TeacherSearchProviderProps {
  children: ReactNode;
}

export const TeacherSearchProvider: React.FC<TeacherSearchProviderProps> = ({
  children
}: TeacherSearchProviderProps) => {
  const [searchType, setSearchType] = useState<InstitutionTypeFilter>(
    getInitialSearchTypeFromLocation
  );
  const [selectedCourse, setSelectedCourse] = useState<Course | undefined>();
  const [selectedInstitution, setSelectedInstitution] = useState<Institution | undefined>();
  const {
    institutionCourses,
    institutionCoursesLoading,
    setInstitutionId: setCoursesInstitutionId
  } = useCoursesQuery(selectedInstitution?.value ?? '', searchType);

  const { institutions, institutionsLoading } = useInstitutionsQuery(searchType);
  const [recentCourses, setRecentCourses] = useState<Course[]>([]);
  const [filters, setFiltersState] = useState<TeachersUIFilters>({});

  const RECENT_COURSES_STORAGE_KEY = 'recentCourses';

  useEffect(() => {
    const storedRecentCourses = localStorage.getItem(RECENT_COURSES_STORAGE_KEY);

    if (storedRecentCourses) {
      setRecentCourses(JSON.parse(storedRecentCourses));
    }
  }, []);

  const addRecentCourse = (newCourse: Course | undefined) => {
    if (newCourse) {
      setTimeout(() => {
        setRecentCourses((prevCourses) => {
          const updatedCourses = prevCourses.filter((c) => c.value !== newCourse.value);

          updatedCourses.unshift(newCourse);

          const newRecentCourses = updatedCourses.slice(0, 10);

          localStorage.setItem(RECENT_COURSES_STORAGE_KEY, JSON.stringify(newRecentCourses));

          return newRecentCourses;
        });
      }, 1000); // some delay
    }
  };

  const clearRecentCourses = () => {
    setRecentCourses([]);
    localStorage.removeItem(RECENT_COURSES_STORAGE_KEY);
  };

  const handleSetInstitution = (institutionToSet: Institution | undefined) => {
    setSelectedInstitution(institutionToSet);
    setSelectedCourse(undefined);
    setCoursesInstitutionId(institutionToSet?.value ?? '');
  };

  const handleSetSearchType = useCallback(
    (type: InstitutionTypeFilter) => {
      setSearchType((prev) => {
        if (prev === type) {
          return prev;
        }

        queueMicrotask(() => {
          setSelectedInstitution(undefined);
          setSelectedCourse(undefined);
          setCoursesInstitutionId('');
        });

        return type;
      });
    },
    [setCoursesInstitutionId]
  );

  const setFilters = useCallback((newFilters: Partial<TeachersUIFilters>) => {
    setFiltersState((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const clearFilter = useCallback((key: keyof TeachersUIFilters) => {
    setFiltersState((prev) => {
      const { [key]: ignored, ...rest } = prev;

      return rest;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const value = useMemo(
    () => ({
      setCourse: setSelectedCourse,
      selectedCourse,
      institutionCourses: selectedInstitution ? institutionCourses : undefined,
      institutionCoursesLoading: selectedInstitution ? institutionCoursesLoading : false,
      institutions,
      setInstitution: handleSetInstitution,
      selectedInstitution,
      institutionsLoading,
      searchType,
      setSearchType: handleSetSearchType,
      recentCourses,
      addRecentCourse,
      clearRecentCourses,
      filters,
      setFilters,
      clearFilter,
      clearAllFilters
    }),
    [
      setSelectedCourse,
      selectedCourse,
      institutionCourses,
      institutionCoursesLoading,
      institutions,
      setSelectedInstitution,
      selectedInstitution,
      institutionsLoading,
      searchType,
      handleSetSearchType,
      recentCourses,
      addRecentCourse,
      clearRecentCourses,
      filters,
      setFilters,
      clearFilter,
      clearAllFilters
    ]
  );

  return <TeacherSearchContext.Provider value={value}>{children}</TeacherSearchContext.Provider>;
};

export default TeacherSearchProvider;
